// ============================================
// 视觉规则求值（阶段5：绑定配置 UI 的运行时核心）
// ruleConfig = { ranges: [{ min, max, color }], defaultColor? }
// resolveVisualRule(value, rule) → 命中区间返回对应色，否则 defaultColor / null
// ============================================

export interface ColorRangeRule {
  min: number;
  max: number;
  /** 井号 hex 颜色，如 #ff4d4f（或 rgb 字符串） */
  color: string;
}

export interface HeightRule {
  /** 点位映射区间：min 对应 0%，max 对应 100% */
  min?: number;
  max?: number;
  /** 罐体高度（液体盒最大高度的场景单位，默认 3） */
  tankH?: number;
}

export interface RotationSpeedRule {
  /** 点位满值时每秒旋转圈数（默认 0.5） */
  maxRps?: number;
  /** 旋转轴：y = 风机/表盘，x / z 备用 */
  axis?: 'x' | 'y' | 'z';
}

export interface ActionRule {
  /** GLB 骨骼动画名（ModelViewer useAnimations names） */
  name?: string;
  /** 触发阈值：点位 > 该值播放，<= 停止（默认 0.5） */
  threshold?: number;
}

export interface VisualRuleConfig {
  ranges?: ColorRangeRule[];
  defaultColor?: string;
  /** 液位（B级动画 7.2）：点位 → 0..1 归一化液位 */
  height?: HeightRule;
  /** 风机/传送带（7.4）：点位 → 连续旋转 */
  rotationSpeed?: RotationSpeedRule;
  /** 骨骼动画（7.3）：点位 → action.play/pause */
  action?: ActionRule;
}

export const VISUAL_TYPES = [
  { value: 'color', label: '数值→颜色' },
  { value: 'height', label: '点位→液位' },
  { value: 'rotationSpeed', label: '点位→转速(风机/转盘)' },
  { value: 'action', label: '点位→骨骼动画(开关)' },
] as const;

export const VISUAL_TYPE_LABEL: Record<string, string> = {
  color: '数值→颜色',
  height: '点位→液位',
  rotationSpeed: '点位→转速',
  action: '点位→骨骼动画',
};

export function parseRuleConfig(raw?: Record<string, unknown> | null): VisualRuleConfig {
  if (!raw || typeof raw !== 'object') return {};
  const ranges = (Array.isArray(raw.ranges) ? raw.ranges : [])
    .filter((r): r is ColorRangeRule => !!r && typeof r === 'object')
    .map((r) => ({
      min: Number(r.min),
      max: Number(r.max),
      color: String(r.color),
    }))
    .filter((r) => Number.isFinite(r.min) && Number.isFinite(r.max) && r.color);
  const pick = <T>(key: string): T | undefined =>
    raw[key] && typeof raw[key] === 'object' ? (raw[key] as T) : undefined;
  return {
    ranges,
    defaultColor: typeof raw.defaultColor === 'string' ? raw.defaultColor : undefined,
    height: pick<HeightRule>('height'),
    rotationSpeed: pick<RotationSpeedRule>('rotationSpeed'),
    action: pick<ActionRule>('action'),
  };
}

/** 阈值-颜色区间求值：首个命中区间即返回；无命中返回 defaultColor；都无返回 null */
export function resolveVisualRule(
  value: number | string | boolean | undefined,
  raw?: Record<string, unknown> | null,
): string | null {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  const rule = parseRuleConfig(raw);
  for (const r of rule.ranges ?? []) {
    if (num >= r.min && num <= r.max) return r.color;
  }
  return rule.defaultColor ?? null;
}

/** 液位归一化（7.2）：点位 → 0..1（clamp；区间外按比例结算，默认 min=0/max=max(tankH 参考,100) 兜底用 0..100） */
export function resolveHeightRatio(
  value: number | string | boolean | undefined,
  raw?: Record<string, unknown> | null,
): number {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  const rule = parseRuleConfig(raw);
  const min = rule.height?.min ?? 0;
  const max = rule.height?.max ?? (min + 100 > min ? min + 100 : 100);
  if (max <= min) return 0;
  return Math.max(0, Math.min(1, (num - min) / (max - min)));
}

/** 风机转速（7.4）：点位 → 每秒弧度（默认满值 0.5 圈/秒） */
export function resolveRotationSpeed(
  value: number | string | boolean | undefined,
  raw?: Record<string, unknown> | null,
): number {
  const num = Number(value);
  const rule = parseRuleConfig(raw);
  const maxRps = rule.rotationSpeed?.maxRps ?? 0.5;
  if (!Number.isFinite(num) || num <= 0 || maxRps <= 0) return 0;
  return num * (Math.PI * 2 * maxRps * 0.01); // 点位 100 ⇒ maxRps 圈/秒
}

/** 骨骼动画触发（7.3）：点位 > threshold → 播放 */
export function shouldPlayAction(
  value: number | string | boolean | undefined,
  raw?: Record<string, unknown> | null,
): boolean {
  const rule = parseRuleConfig(raw);
  const threshold = rule.action?.threshold ?? 0.5;
  const num = Number(value);
  return Number.isFinite(num) && num > threshold;
}